import React, { useEffect, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { ChevronUp, Crown, MoreVertical } from 'lucide-react';
import type { Agents } from '@/types/Agents';
import { Link } from 'react-router-dom';
import Helper from '@/lib/helper';
import { toast } from 'sonner';
import api from '@/lib/axiosInstance';
import { useSelector } from 'react-redux';
import type { RootState } from '@/lib/redux/store';

interface MemberNodeProps {
    member: Agents;
    isLast: boolean;
    showArrow?: boolean;
}

const MemberNode: React.FC<MemberNodeProps> = ({ member, isLast, showArrow = true }) => (
    <div className="flex flex-col items-center">
        <div className="flex flex-col items-center gap-2">
            <Link to={`/agents/edit/${member.id}`}>
                <Avatar className="h-12 w-12 border">
                    <AvatarImage src={`${import.meta.env.VITE_AWS_S3}/${member.avatar}`}/>
                    <AvatarFallback className="bg-gray-200 text-gray-500 text-sm">
                        {member?.firstName?.[0]?.toUpperCase() ?? 'N'}
                        {member?.lastName?.[0]?.toUpperCase() ?? ''}
                    </AvatarFallback>
                </Avatar>
            </Link>
            <div className="text-center">
                <p className="text-sm font-medium">{member.firstName} {member.lastName}</p>
                <p className="text-xs text-muted-foreground">{member.renNo}</p>
            </div>
        </div>
        {
            showArrow && !isLast && (
                <div className="flex flex-col items-center my-3">
                    <ChevronUp className="w-5 h-5 text-muted-foreground" />
                </div>
            )
        }
    </div>
);

const Tree = () => {
    const agent = useSelector((state: RootState) => state.auth.agent);
    const MAX_UPLINE_LEVEL = 5;
    const [viewMode, setViewMode] = useState<'upline' | 'downline'>('upline');
    const [mlmUpline, setMlmUpline] = useState<Agents[] | null>(null);
    const [mlmDownline, setMlmDownline] = useState<any[] | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    const getAgentGenerationTree = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/me/tree`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setMlmUpline(response.data.mlmUpline || []);
            setMlmDownline(response.data.mlmDownline || []);
        } catch (err : any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
            setMlmUpline([]);
            setMlmDownline([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        getAgentGenerationTree();
    }, []);

    if (loading) {
        return (
            <div className="p-6 max-w-7xl mx-auto flex justify-center items-center min-h-[400px]">
                <p className="text-muted-foreground">Loading tree...</p>
            </div>
        );
    }

    if (!mlmUpline || !mlmDownline) {
        return (
            <div className="p-6 max-w-7xl mx-auto flex justify-center items-center min-h-[400px]">
                <p className="text-muted-foreground">Failed to load tree data</p>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex gap-2 mb-6">
                <Button
                onClick={() => setViewMode('upline')}
                variant={viewMode === 'upline' ? 'default' : 'outline'}

                size="sm">
                    Upline
                </Button>
                <Button
                onClick={() => setViewMode('downline')}
                variant={viewMode === 'downline' ? 'default' : 'outline'}

                size="sm">
                    Downline
                </Button>
            </div>
            <div className="flex flex-col items-center">
                {
                    viewMode === 'upline' && (
                        <div className="flex flex-col items-center">
                            {
                                mlmUpline.length > 0 && (
                                    <div className="mb-4">
                                        {
                                            mlmUpline.length >= MAX_UPLINE_LEVEL ? (
                                                <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full">
                                                    <Crown className="w-4 h-4 text-amber-600" />
                                                    <span className="text-xs font-medium text-amber-700">
                                                        Top Level Reached (Level {MAX_UPLINE_LEVEL})
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2 px-4 py-2">
                                                    <span className="text-xs font-medium">
                                                        Level {mlmUpline.length} of {MAX_UPLINE_LEVEL}
                                                    </span>
                                                </div>
                                            )
                                        }
                                    </div>
                                )
                            }
                            {
                                mlmUpline.length > 0 ? (
                                    <>
                                        {
                                            [...mlmUpline].reverse().map((member, index) => (
                                                <MemberNode 
                                                key={member.id} 
                                                member={member} 
                                                isLast={false}
                                                showArrow={true}/>
                                            ))
                                        }
                                    </>
                                ) : (
                                    <div className="flex flex-col items-center mb-4">
                                        <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-full mb-4">
                                            <MoreVertical className="w-4 h-4 text-gray-400" />
                                            <span className="text-xs font-medium text-gray-600">
                                                No Upline (Level 0)
                                            </span>
                                        </div>
                                        <div className="w-px h-8 bg-border" />
                                    </div>
                                )
                            }
                            <div className="flex flex-col items-center gap-2 p-3">
                                <Avatar className="h-14 w-14">
                                    <AvatarImage src={`${import.meta.env.VITE_AWS_S3}/${agent.avatar}`}/>
                                    <AvatarFallback className="text-sm font-semibold bg-primary text-primary-foreground">
                                        {agent?.firstName?.[0]?.toUpperCase() ?? 'N'}
                                        {agent?.lastName?.[0]?.toUpperCase() ?? ''}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="text-center">
                                    <p className="text-sm font-semibold">{agent.firstName} {agent.lastName}</p>
                                    <p className="text-xs text-muted-foreground">{agent.renNo}</p>
                                    <p className="text-xs text-primary font-medium mt-1">You</p>
                                </div>
                            </div>
                        </div>
                    )
                }
                {
                    viewMode === 'downline' && (
                        <div className="flex flex-col items-center w-full">
                            <div className="flex flex-col items-center gap-2 p-3 mb-2">
                                <Avatar className="h-14 w-14">
                                    <AvatarImage src={`${import.meta.env.VITE_AWS_S3}/${agent.avatar}`}/>
                                    <AvatarFallback className="text-sm font-semibold bg-primary text-primary-foreground">
                                        {agent?.firstName?.[0]?.toUpperCase() ?? 'N'}
                                        {agent?.lastName?.[0]?.toUpperCase() ?? ''}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="text-center">
                                    <p className="text-sm font-semibold">{agent.firstName} {agent.lastName}</p>
                                    <p className="text-xs text-muted-foreground">{agent.renNo}</p>
                                    <p className="text-xs text-primary font-medium mt-1">You</p>
                                </div>
                            </div>
                            {
                                mlmDownline.length > 0 ? (
                                    <div className="w-full space-y-8">
                                        {
                                            mlmDownline.map((levelData, levelIndex) => (
                                                <div key={levelIndex} className="flex flex-col items-center">
                                                    <div className="w-px h-8 bg-border my-2" />
                                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-8 gap-y-6">
                                                        {
                                                            levelData.agents.map((member: Agents) => (
                                                                <div key={member.id} className="flex flex-col items-center gap-2 relative">
                                                                    <Link to={`/agents/edit/${member.id}`}>
                                                                        <Avatar className="h-12 w-12 border">
                                                                            <AvatarImage src={`${import.meta.env.VITE_AWS_S3}/${member.avatar}`}/>
                                                                            <AvatarFallback className="text-sm font-medium bg-gray-200 text-gray-500">
                                                                                {member?.firstName?.[0]?.toUpperCase() ?? 'N'}
                                                                                {member?.lastName?.[0]?.toUpperCase() ?? ''}
                                                                            </AvatarFallback>
                                                                        </Avatar>
                                                                    </Link>
                                                                    <div className="text-center">
                                                                        <p className="text-sm font-medium">{member.firstName} {member.lastName}</p>
                                                                        <p className="text-xs text-muted-foreground">{member.renNo}</p>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        }
                                                    </div>
                                                </div>
                                            ))
                                        }
                                    </div>
                                ) : (
                                    <div className="text-sm text-muted-foreground mt-8">No downline</div>
                                )
                            }
                        </div>
                    )
                }
            </div>
        </div>
    );
};

export default Tree;